// Tracks whether the API is reachable so the UI can tell the user what is
// going on instead of failing silently.
//
// `services/api.ts` reports every request outcome here. While offline, the
// public `GET /healthz` endpoint is probed every RETRY_INTERVAL_MS so there is
// always a "retrying in Ns" countdown and a definite "back online" moment, even
// when nothing is waiting to be saved. This timer is independent of the 30s
// autosave: on reconnect `ConnectivityToasts` flushes pending edits directly.
import { useSyncExternalStore } from 'react'
import { API_ROOT } from '@/services/endpoints'

export const RETRY_INTERVAL_MS = 10_000

export type ConnectivityStatus = 'online' | 'offline'

export type ConnectivityState = {
	status: ConnectivityStatus
	/** epoch ms of the next automatic probe while offline, else null */
	nextRetryAt: number | null
	/** a probe is currently in flight */
	probing: boolean
	/** epoch ms when we first noticed the outage, else null */
	offlineSince: number | null
}

const ONLINE: ConnectivityState = {
	status: 'online',
	nextRetryAt: null,
	probing: false,
	offlineSince: null,
}

let state: ConnectivityState = ONLINE
let timer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

const emit = () => listeners.forEach((fn) => fn())
const set = (next: Partial<ConnectivityState>) => {
	state = { ...state, ...next }
	emit()
}

function clearTimer() {
	if (timer !== null) {
		clearTimeout(timer)
		timer = null
	}
}

function scheduleProbe() {
	clearTimer()
	timer = setTimeout(() => void probe(), RETRY_INTERVAL_MS)
	set({ nextRetryAt: Date.now() + RETRY_INTERVAL_MS })
}

/** Cheap reachability check against the public health endpoint. */
async function probe(): Promise<void> {
	if (state.status !== 'offline' || state.probing) return
	clearTimer()
	set({ probing: true, nextRetryAt: null })
	try {
		const res = await fetch(`${API_ROOT}/healthz`, { credentials: 'include', cache: 'no-store' })
		if (res.ok) {
			connectivity.reportSuccess()
			return
		}
	} catch {
		// unreachable: fall through and schedule the next probe
	}
	if (state.status === 'offline') {
		set({ probing: false })
		scheduleProbe()
	}
}

export const connectivity = {
	snapshot(): ConnectivityState {
		return state
	},

	subscribe(fn: () => void): () => void {
		listeners.add(fn)
		return () => {
			listeners.delete(fn)
		}
	},

	/** A request could not reach the API (network error, gateway 502/503/504). */
	reportFailure(): void {
		if (state.status === 'offline') return
		state = { status: 'offline', nextRetryAt: null, probing: false, offlineSince: Date.now() }
		scheduleProbe()
	},

	/** Any real answer from the API, success or error, proves it is reachable. */
	reportSuccess(): void {
		if (state.status === 'online') return
		clearTimer()
		state = ONLINE
		emit()
	},

	/** User pressed "Retry now": probe immediately instead of waiting for the timer. */
	retryNow(): void {
		if (state.status !== 'offline' || state.probing) return
		void probe()
	},

	/** Exposed for tests only. */
	__resetForTests(): void {
		clearTimer()
		listeners.clear()
		state = ONLINE
	},
}

export function useConnectivity(): ConnectivityState {
	return useSyncExternalStore(connectivity.subscribe, connectivity.snapshot, connectivity.snapshot)
}
